import {Component, inject, Input, ViewChild} from '@angular/core';
import {FormGroup, ReactiveFormsModule} from '@angular/forms';
import {
    IBaseCRUModalComponent,
    IBaseCRUModalResultBase
} from '../../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {PrimeNgImportsModule} from '../../../../../sdk/src/prime-ng-imports';
import {SdkImportsModule} from '../../../../../sdk/src/sdk-imports';
import {FileEntryModelExtension} from '../../../api-extensions/models/file-entry.model-extension';
import {FileUtils} from '../../../utils/file.utils';
import {CpmCFileApiService} from '../../../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-file.api-service';
import {firstValueFrom, lastValueFrom, tap} from 'rxjs';
import {FileUpload} from 'primeng/fileupload';
import {HttpClient, HttpEvent, HttpEventType, HttpHeaders} from '@angular/common/http';

export class UploadFileModalConfig {
    constructor(public postUploadAction?: (fileEntries: FileEntryModelExtension[]) => Promise<void>) {}
}
export class UploadFileModalResult {
    constructor(public items?: FileEntryModelExtension[]) {}
}

/**
 * Upload file modal
 */
@Component({
    selector: 'app-upload-file-modal',
    templateUrl: './upload-file-modal.component.html',
    imports: [ReactiveFormsModule, PrimeNgImportsModule, SdkImportsModule]
})
export class UploadFileModalComponent extends IBaseCRUModalComponent<
    FileEntryModelExtension,
    UploadFileModalComponent,
    UploadFileModalConfig,
    UploadFileModalResult
> {
    protected readonly FileUtils = FileUtils;

    // ===============================================================
    // ===============================================================

    private readonly http: HttpClient = inject(HttpClient);

    // ===============================================================
    // ===============================================================

    @ViewChild(FileUpload, {static: true})
    protected _fileUpload!: FileUpload;
    protected _files: File[] = [];

    // ===============================================================
    // ===============================================================

    @Input()
    public postUploadAction?: (fileEntries: FileEntryModelExtension[]) => Promise<void>;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure('Upload file', (model: FileEntryModelExtension) => model.name ?? '<Unknown file>');
        this.config.width = '30rem';
        this.contentMinHeight = '4rem';
        this.config.header = 'Upload file(s)';
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling is done in the parent)
     */
    protected async onReady(): Promise<void> {
        this.form = new FormGroup({});

        if (this.config.data?.extended?.postUploadAction)
            this.postUploadAction = this.config.data.extended.postUploadAction;
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * Can the modal be submitted ?
     * @returns True if the modal can be submitted
     */
    public override canSubmit(): boolean {
        return this._fileUpload?.files?.length > 0;
    }
    /**
     * On submit
     */
    protected async onSubmit() {
        if (!this.canSubmit()) {
            return;
        }

        await this.onSubmitConfirmed();
    }
    /**
     * On submit (After potential validation)
     */
    private async onSubmitConfirmed() {
        if (!this.canSubmit()) {
            return;
        }

        try {
            const filesToUpload = this._fileUpload.files;
            const uploadedFileEntries: FileEntryModelExtension[] = [];
            const totalFiles = filesToUpload.length;

            this.showLoading = true;
            this._fileUpload.upload();

            for (let i = 0; i < filesToUpload.length; i++) {
                const fileToUpload = filesToUpload[i];
                const fileNumber = i + 1;

                // Create file entry for upload
                const creationResult = await firstValueFrom(
                    CpmCFileApiService.postCreateFileEntryForUpload({
                        filename: fileToUpload.name,
                        size: fileToUpload.size
                    })
                );

                const headers = new HttpHeaders({
                    'Content-Type': fileToUpload.type || 'application/octet-stream'
                });

                // Upload the file
                await lastValueFrom(
                    this.http
                        .request('put', creationResult.upload_url, {
                            body: fileToUpload,
                            headers: headers,
                            reportProgress: true,
                            observe: 'events'
                        })
                        .pipe(
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            tap(async (event: HttpEvent<any>) => {
                                switch (event.type) {
                                    case HttpEventType.Response:
                                        if (event['status'] >= 200 && event['status'] < 300) {
                                            if (this._fileUpload.fileLimit) {
                                                this._fileUpload.uploadedFileCount += 1;
                                            }

                                            this._fileUpload.onUpload.emit({
                                                originalEvent: event,
                                                files: [fileToUpload]
                                            });
                                        } else {
                                            this._fileUpload.onError.emit({files: [fileToUpload]});

                                            this.globalsService.messageService.add({
                                                key: this.overrideMessagesKey,
                                                severity: 'error',
                                                summary: 'Failed to upload file',
                                                detail: `Failed to upload ${fileToUpload.name}`
                                            });
                                        }
                                        break;

                                    case HttpEventType.UploadProgress: {
                                        if (event.loaded && event.total) {
                                            const uploaded = event.loaded;
                                            const total = event.total;
                                            const formattedUploaded = FileUtils.formatFileSize(uploaded);
                                            const formattedTotal = FileUtils.formatFileSize(total);
                                            const progress = Math.round((uploaded * 100) / total);

                                            this.loadingStr = `Uploading file ${fileNumber}/${totalFiles}: ${fileToUpload.name} ${progress}% ${formattedUploaded} / ${formattedTotal}`;
                                            this._fileUpload.progress = progress;
                                        }

                                        this._fileUpload.onProgress.emit({
                                            originalEvent: event,
                                            progress: this._fileUpload.progress
                                        });
                                        break;
                                    }
                                }
                            })
                        )
                );

                const fileEntry: FileEntryModelExtension = creationResult.file_store_entry;
                fileEntry._formated_size = FileUtils.formatFileSize(fileEntry.size);
                uploadedFileEntries.push(fileEntry);
            }

            this._fileUpload.uploading = false;
            this._fileUpload.progress = 0;
            this._fileUpload.uploadedFiles = [...this._fileUpload.uploadedFiles, ...filesToUpload];
            this._fileUpload.clear();

            if (this.postUploadAction) await this.postUploadAction(uploadedFileEntries);

            this.globalsService.messageService.add({
                key: this.overrideMessagesKey,
                severity: 'success',
                summary: 'Uploaded files',
                detail: `${uploadedFileEntries.length} file(s) uploaded successfully`
            });

            this.ref.close(
                new IBaseCRUModalResultBase<UploadFileModalResult>(this.dialogOpenMode, {
                    items: uploadedFileEntries
                })
            );

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            console.log(err);
            this.globalsService.messageService.add({
                key: this.overrideMessagesKey,
                severity: 'error',
                summary: 'Failed to upload file',
                detail: err?.error?.message ?? 'Unknown'
            });

            this.showLoading = false;
        }
    }

    // ===============================================================
    // ===============================================================

    /**
     * Dummy custom upload function
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected customUploadDummy() {}
}
